"use client";

import { MapPin, Car, Plane, Building2 } from "lucide-react";
import dynamic from "next/dynamic";

// Dynamically import MapView to avoid SSR issues with Leaflet
const MapView = dynamic(() => import("@/components/properties/map-view"), {
    ssr: false,
    loading: () => <div className="w-full h-full bg-secondary/10 animate-pulse" />
});

interface CommunityLocationProps {
    locationName: string;
    properties?: any[]; // Allow passing properties array
}

export function CommunityLocation({ locationName, properties = [] }: CommunityLocationProps) {
    return (
        <section className="py-0 relative">
            {/* Map Container - Replaced iframe with MapView */}
            <div className="w-full h-[500px] md:h-[600px] bg-secondary/10 relative">
                <MapView
                    properties={properties}
                    className="w-full h-full"
                // No onClose prop needed as it's embedded
                />

                {/* Connectivity overlay card - Kept as is for context */}
                <div className="absolute top-8 left-6 md:top-12 md:left-12 z-[500] pointer-events-none">
                    {/* Added pointer-events-none to container and auto to children so map is clickable behind, 
                         but simplified here: z-index 500 is above map's 400. 
                         Let's keep it simple: Map is interactive, overlay sits on top. 
                     */}
                    <div className="bg-white/95 backdrop-blur-md p-8 md:p-10 max-w-sm rounded-xl shadow-2xl border border-primary/5 pointer-events-auto">
                        <div className="flex items-center gap-3 mb-6 text-primary">
                            <MapPin className="w-5 h-5" />
                            <span className="text-xs font-bold tracking-[0.2em] uppercase">Prime Location</span>
                        </div>

                        <h3 className="font-serif text-3xl text-primary mb-2">{locationName}</h3>
                        <p className="text-muted-foreground text-sm mb-8">
                            Strategically positioned to offer the best of both worlds: serene community living with easy access to the city's vibrant pulse.
                        </p>

                        <div className="space-y-6">
                            <div className="flex items-center gap-4 group">
                                <div className="w-10 h-10 rounded-full bg-secondary/10 flex items-center justify-center text-primary group-hover:bg-primary group-hover:text-white transition-colors">
                                    <Plane className="w-4 h-4" />
                                </div>
                                <div>
                                    <span className="block text-primary font-medium">Dubai Int. Airport</span>
                                    <span className="text-xs text-muted-foreground uppercase tracking-wider">25 Minutes</span>
                                </div>
                            </div>

                            <div className="flex items-center gap-4 group">
                                <div className="w-10 h-10 rounded-full bg-secondary/10 flex items-center justify-center text-primary group-hover:bg-primary group-hover:text-white transition-colors">
                                    <Building2 className="w-4 h-4" />
                                </div>
                                <div>
                                    <span className="block text-primary font-medium">Downtown Dubai</span>
                                    <span className="text-xs text-muted-foreground uppercase tracking-wider">15 Minutes</span>
                                </div>
                            </div>

                            <div className="flex items-center gap-4 group">
                                <div className="w-10 h-10 rounded-full bg-secondary/10 flex items-center justify-center text-primary group-hover:bg-primary group-hover:text-white transition-colors">
                                    <Car className="w-4 h-4" />
                                </div>
                                <div>
                                    <span className="block text-primary font-medium">Dubai Marina</span>
                                    <span className="text-xs text-muted-foreground uppercase tracking-wider">20 Minutes</span>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </section>
    );
}
